import { PayloadAction } from '@reduxjs/toolkit';
import { camelCase } from 'lodash';
import createAppSlice from 'app/createAppSlice';
import { GENERAL } from 'utils/constants';
import { AlertsType, AlertGroupCounts } from './types';

const initialState: AlertsType = {
  content: [],
  page: 0,
  pageSize: GENERAL.alertsPerPage,
  total: 0,
  totalUnreadCount: 0,
  // Empty array = "All" — no category chips selected.
  alertFilterTypes: [],
  alertGroupCounts: {},
  alertGroupCountsLoaded: false,
  resolved: false,
  filterChanging: false,
};

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

const alertsSlice = createAppSlice({
  name: 'alerts',
  initialState,
  reducers: {
    updateAlertsData: (state, action: PayloadAction<AlertsType>) => {
      const { content, page, pageSize, total, totalUnreadCount } =
        action.payload;
      // Honor the order returned by /api/client/alerts verbatim — no
      // client-side sorting. The API already paginates and orders the list.
      state.content = content;
      state.page = page;
      state.pageSize = pageSize;
      state.total = total;
      state.totalUnreadCount = totalUnreadCount;
      state.filterChanging = false;
    },
    updateViewStatus: (state, action: PayloadAction<string>) => {
      const alertIndex = state.content.findIndex(
        (alert) => alert.id === action.payload,
      );
      if (alertIndex === -1) return;
      const alert = state.content[alertIndex];
      // Idempotent: opening an already-viewed alert must not decrement
      // the counts again (accordion re-expand, StrictMode double-fire).
      if (alert.viewed) return;
      alert.viewed = true;
      state.totalUnreadCount = Math.max(0, state.totalUnreadCount - 1);

      // Decrement the matching group's countUnread so the header badge
      // (derived from alertGroupCounts via selectAlertsMetadata) updates
      // instantly — no /alerts/group refetch required. Mirrors Angular's
      // `camelCase(alert.group)` rule (new-alert-details-header.component
      // .ts:38) — e.g. "Social Media Monitoring" → "socialMediaMonitoring".
      //
      // Backend quirk: the alerts list emits both "Social Media" and
      // "Social Media Monitoring" for the same category (same for
      // "SSN Trace" / "SSN Trace Adult"), and the group endpoint returns
      // BOTH keys — one with real counts and the other as a zeroed-out
      // placeholder. Prefer whichever key actually has non-zero counts;
      // fall back to the camelCased key when neither is populated.
      const primaryKey = alert.group ? camelCase(alert.group) : '';
      const SIBLING_KEY: Record<string, string> = {
        socialMedia: 'socialMediaMonitoring',
        socialMediaMonitoring: 'socialMedia',
        ssnTrace: 'ssnTraceAdult',
        ssnTraceAdult: 'ssnTrace',
      };
      const sibling = SIBLING_KEY[primaryKey];
      const primaryHasCounts =
        (state.alertGroupCounts[primaryKey]?.total ?? 0) > 0;
      const siblingHasCounts = sibling
        ? (state.alertGroupCounts[sibling]?.total ?? 0) > 0
        : false;
      const resolvedKey = primaryHasCounts
        ? primaryKey
        : siblingHasCounts
          ? sibling
          : primaryKey;
      const group = state.alertGroupCounts[resolvedKey];
      if (group && typeof group.countUnread === 'number') {
        group.countUnread = Math.max(0, group.countUnread - 1);
      }
    },
    setAlertFilterTypes: (state, action: PayloadAction<string[]>) => {
      // Normalise + dedupe. Empty array = "All". `state.alertFilterTypes`
      // can be undefined when redux-persist rehydrates a snapshot from
      // before the multi-select refactor (old shape was `alertFilterType:
      // string`) — default to [] for the equality check and the write.
      const current = state.alertFilterTypes ?? [];
      const next = Array.from(new Set(action.payload ?? []));
      if (!arraysEqual(current, next)) {
        state.filterChanging = true;
      }
      state.alertFilterTypes = next;
    },
    setAlertGroupCounts: (state, action: PayloadAction<AlertGroupCounts>) => {
      state.alertGroupCounts = action.payload;
      state.alertGroupCountsLoaded = true;
    },
    setResolved: (state, action: PayloadAction<boolean>) => {
      state.resolved = action.payload;
    },
    alertsReset: () => initialState,
  },
});

export const {
  updateAlertsData,
  updateViewStatus,
  setAlertFilterTypes,
  setAlertGroupCounts,
  setResolved,
  alertsReset,
} = alertsSlice.actions;
export default alertsSlice.reducer;
